import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage, useAuth, api } from '@/lib/context';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Sparkles, FileText, Download, Copy, Check, Loader2, 
  Globe, AlertCircle, ArrowLeft, CheckCircle2, Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

// Languages supported for CV translation - using i18n keys
const CV_LANGUAGES = [
  { code: 'en', nameKey: 'language.names.en' },
  { code: 'fi', nameKey: 'language.names.fi' },
  { code: 'et', nameKey: 'language.names.et' },
  { code: 'no', nameKey: 'language.names.no' },
  { code: 'de', nameKey: 'language.names.de' },
  { code: 'sv', nameKey: 'language.names.sv' },
  { code: 'ru', nameKey: 'language.names.ru' },
  { code: 'pl', nameKey: 'language.names.pl' },
  { code: 'uk', nameKey: 'language.names.uk' },
];

// Navigation Component
const NavBar = () => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">
              {t('nav.jobs')}
            </Link>
            <Link to="/cv-enhance" className="text-foreground font-medium">
              {t('nav.cvEnhance')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link to={user.role === 'company' ? '/company/dashboard' : user.role === 'admin' ? '/admin' : '/worker/dashboard'}>
                  <Button variant="outline" size="sm">{t('nav.dashboard')}</Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }}>
                  {t('auth.logout')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">{t('auth.login')}</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm">{t('auth.register')}</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// Main CV Enhancement Page
export const CVEnhancePage = () => {
  const { t } = useLanguage();
  const [cvText, setCvText] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('');
  const [enhancing, setEnhancing] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleEnhance = async () => {
    if (!cvText.trim()) {
      toast.error(t('cvEnhance.enterCvText'));
      return;
    }

    setEnhancing(true);
    setResult(null);

    try {
      const res = await api.enhanceCv({
        cv_text: cvText,
        target_language: targetLanguage || null
      });
      setResult(res.data);
      toast.success(t('cvEnhance.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setEnhancing(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(t('common.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleDownload = (text, filename) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(t('cvEnhance.downloaded'));
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand/10 mb-4">
            <Sparkles className="h-8 w-8 text-brand" />
          </div>
          <h1 className="text-3xl font-bold mb-2">
            {t('cvEnhance.title')}
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            {t('cvEnhance.subtitle')}
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Input Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('cvEnhance.yourCv')}
              </CardTitle>
              <CardDescription>
                {t('cvEnhance.pasteInstructions')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Textarea
                  placeholder={t('cvEnhance.placeholder')}
                  value={cvText}
                  onChange={(e) => setCvText(e.target.value)}
                  rows={12}
                  className="font-mono text-sm"
                  data-testid="cv-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {cvText.length} {t('common.characters')}
                </p>
              </div>

              {/* Language selector */}
              <div>
                <Label className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  {t('cvEnhance.translateTo')}
                </Label>
                <Select value={targetLanguage} onValueChange={setTargetLanguage}>
                  <SelectTrigger className="mt-1" data-testid="language-select">
                    <SelectValue placeholder={t('cvEnhance.selectLanguage')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('cvEnhance.noTranslation')}</SelectItem>
                    {CV_LANGUAGES.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>{t(lang.nameKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={handleEnhance} 
                disabled={enhancing || !cvText.trim()}
                className="w-full"
                data-testid="enhance-btn"
              >
                {enhancing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('cvEnhance.enhancing')}
                  </>
                ) : (
                  <>
                    <Wand2 className="mr-2 h-4 w-4" />
                    {t('cvEnhance.enhanceBtn')}
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>

          {/* Result Section */}
          <Card className={!result ? 'opacity-50' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-brand" />
                {t('cvEnhance.enhanced')}
              </CardTitle>
              {result && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {t('cvEnhance.qualityScore')}:
                  </span>
                  <Badge variant={result.quality_score >= 70 ? 'default' : 'secondary'}>
                    {result.quality_score}/100
                  </Badge>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {!result ? (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Sparkles className="h-12 w-12 mx-auto mb-2 opacity-20" />
                    <p>{t('cvEnhance.resultPlaceholder')}</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Improvements */}
                  {result.improvements?.length > 0 && (
                    <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3">
                      <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-2">
                        {t('cvEnhance.improvements')}:
                      </p>
                      <ul className="text-sm text-green-600 dark:text-green-500 space-y-1">
                        {result.improvements.map((imp, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            {imp}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Skills extracted */}
                  {result.skills_extracted?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2">
                        {t('cvEnhance.skillsExtracted')}:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {result.skills_extracted.map((skill, i) => (
                          <Badge key={i} variant="outline">{skill}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Enhanced text */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-medium">
                        {t('cvEnhance.enhancedText')}:
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleCopy(result.enhanced_text)}
                        >
                          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownload(result.enhanced_text, 'enhanced_cv.txt')}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="bg-muted rounded-lg p-3 max-h-48 overflow-y-auto">
                      <pre className="text-sm whitespace-pre-wrap font-mono">
                        {result.enhanced_text}
                      </pre>
                    </div>
                  </div>

                  {/* Translation */}
                  {result.translation && (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-sm font-medium flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          {t('cvEnhance.translation')} ({result.target_language}):
                        </p>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleCopy(result.translation)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownload(result.translation, `cv_${result.target_language}.txt`)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div className="bg-muted rounded-lg p-3 max-h-48 overflow-y-auto">
                        <pre className="text-sm whitespace-pre-wrap font-mono">
                          {result.translation}
                        </pre>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Features section */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="text-center p-6">
            <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="h-6 w-6 text-brand" />
            </div>
            <h3 className="font-semibold mb-1">{t('cvEnhance.feature1Title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('cvEnhance.feature1Desc')}
            </p>
          </div>
          <div className="text-center p-6">
            <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-3">
              <Sparkles className="h-6 w-6 text-brand" />
            </div>
            <h3 className="font-semibold mb-1">{t('cvEnhance.feature2Title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('cvEnhance.feature2Desc')}
            </p>
          </div>
          <div className="text-center p-6">
            <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-3">
              <Globe className="h-6 w-6 text-brand" />
            </div>
            <h3 className="font-semibold mb-1">{t('cvEnhance.feature3Title')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('cvEnhance.feature3Desc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CVEnhancePage;
